import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Tag, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function OrderConfigure() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState({ products: [], addons: [] });
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [property, setProperty] = useState({ name: "", url: "", address: "", notes: "" });
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [useCredit, setUseCredit] = useState(false);

  useEffect(() => { api.get("/catalog").then(({ data }) => setCatalog(data)); }, []);

  const product = catalog.products.find((p) => p.id === productId);
  const toggleAddon = (id) =>
    setSelectedAddons((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const subtotal = (product?.price || 0) + selectedAddons.reduce((sum, id) => {
    const a = catalog.addons.find((x) => x.id === id); return sum + (a?.price || 0);
  }, 0);
  const discount = preview?.discount || 0;
  const creditEligible = !!user && (user.credits || 0) > 0 && ["social-cinematic", "most-popular"].includes(productId);
  const total = useCredit ? 0 : (preview ? preview.total : subtotal);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    if (!user) {
      setApplied({ code: promo.trim().toUpperCase(), pending: true });
      toast.info("Sign in — code will be validated at checkout");
      return;
    }
    await runPreview(promo.trim(), true);
  };

  const runPreview = async (code, notify) => {
    try {
      const { data } = await api.post("/orders/preview", {
        product_id: productId, addons: selectedAddons, promo_code: code,
      });
      if (data.error) {
        setPreview(null); setApplied(null);
        if (notify) toast.error(data.error);
      } else {
        setPreview(data);
        setApplied({ code: code.toUpperCase(), discount: data.discount });
        if (notify) toast.success(data.discount > 0 ? `Code applied — you save $${data.discount.toLocaleString()}` : "Code applied");
      }
    } catch (err) {
      if (notify) toast.error(formatApiError(err.response?.data?.detail) || "Could not apply code");
    }
  };

  useEffect(() => {
    if (applied && !applied.pending && user) runPreview(promo.trim(), false);
    else if (!applied) setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddons]);

  const checkout = async () => {
    if (!user) { navigate("/login", { state: { from: `/order/${productId}` } }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        product_id: productId,
        addons: useCredit ? [] : selectedAddons,
        property,
        promo_code: useCredit ? null : (promo.trim() || null),
        use_credit: useCredit,
        origin_url: window.location.origin,
      });
      if (data.requires_payment) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Complimentary order created — no payment required");
        navigate(`/orders/${data.order.id}/submit`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
    } finally { setBusy(false); }
  };

  if (!product) {
    return (
      <div className="bg-[#090A0F] min-h-screen"><Header />
        <div className="pt-40 text-center text-slate-400">Loading product…</div>
      </div>
    );
  }

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-5xl mx-auto px-6 pt-32 pb-16 grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="fade-up">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">{product.badge}</p>
          <h1 className="font-display text-4xl text-white mb-2">{product.name}</h1>
          <p className="text-slate-400 mb-8">{product.description}</p>

          <div className="space-y-5 mb-10">
            <h3 className="font-display text-2xl text-white">Property details</h3>
            <div>
              <Label className="text-slate-300">Property / listing name</Label>
              <Input value={property.name} onChange={(e) => setProperty({ ...property, name: e.target.value })}
                className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="property-name" />
            </div>
            <div>
              <Label className="text-slate-300">Listing / property URL</Label>
              <Input value={property.url} onChange={(e) => setProperty({ ...property, url: e.target.value })}
                placeholder="https://…" className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="property-url" />
            </div>
            <div>
              <Label className="text-slate-300">Address</Label>
              <Input value={property.address} onChange={(e) => setProperty({ ...property, address: e.target.value })}
                className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="property-address" />
            </div>
            <div>
              <Label className="text-slate-300">Notes / approved features</Label>
              <Textarea value={property.notes} onChange={(e) => setProperty({ ...property, notes: e.target.value })}
                className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="property-notes" />
            </div>
          </div>

          <h3 className="font-display text-2xl text-white mb-4">Add-ons</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {catalog.addons.map((a) => (
              <label key={a.id}
                className={`flex gap-3 p-4 rounded-xl border cursor-pointer transition-all
                  ${selectedAddons.includes(a.id) ? "border-[#D4AF37]/50 bg-[#D4AF37]/10" : "border-slate-800 bg-[#12141D] hover:border-[#D4AF37]/30"}`}
                data-testid={`addon-${a.id}`}>
                <Checkbox checked={selectedAddons.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)}
                  className="mt-1 border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black" />
                <div>
                  <p className="text-white text-sm font-medium">{a.name} <span className="text-[#D4AF37] font-mono">+${a.price}</span></p>
                  <p className="text-xs text-slate-400">{a.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 h-fit glass rounded-2xl p-6 fade-up" data-testid="order-summary">
          <h3 className="font-display text-2xl text-white mb-4">Order summary</h3>
          <div className="flex justify-between text-sm py-2 border-b border-slate-800">
            <span className="text-slate-300">{product.name}</span>
            <span className="font-mono text-white">${product.price.toLocaleString()}</span>
          </div>
          {selectedAddons.map((id) => {
            const a = catalog.addons.find((x) => x.id === id);
            return (
              <div key={id} className="flex justify-between text-sm py-2 border-b border-slate-800/50">
                <span className="text-slate-400">{a.name}</span>
                <span className="font-mono text-slate-300">+${a.price}</span>
              </div>
            );
          })}

          <div className="mt-4">
            {creditEligible && (
              <label className="flex items-center gap-3 p-3 mb-3 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 cursor-pointer" data-testid="use-credit-toggle">
                <Checkbox checked={useCredit}
                  onCheckedChange={(v) => { setUseCredit(!!v); if (v) { setSelectedAddons([]); setPromo(""); setApplied(null); setPreview(null); } }}
                  className="border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black" />
                <div>
                  <p className="text-sm text-white font-medium">Use 1 film credit</p>
                  <p className="text-xs text-slate-400">{user.credits} available · covers this listing film</p>
                </div>
              </label>
            )}
            {!useCredit && (<>
            <Label className="text-slate-300 text-xs">Promo / test code</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="e.g. PROPMOTION100"
                className="bg-[#12141D] border-slate-700 text-white font-mono uppercase" data-testid="promo-input" />
              <Button variant="outline" onClick={applyPromo} className="border-[#D4AF37]/40 text-[#D4AF37] shrink-0" data-testid="apply-promo">
                <Tag className="w-4 h-4" />
              </Button>
            </div>
            {applied?.pending && <p className="text-xs text-[#D4AF37] mt-2 flex items-center gap-1"><Check className="w-3 h-3" /> {applied.code} — validated at checkout</p>}
            {applied && !applied.pending && discount > 0 && (
              <div className="flex justify-between text-sm mt-3 text-emerald-400" data-testid="promo-discount-row">
                <span className="flex items-center gap-1"><Check className="w-3 h-3" /> {applied.code}</span>
                <span className="font-mono">-${discount.toLocaleString()}</span>
              </div>
            )}
            </>)}
            {useCredit && (
              <div className="flex justify-between text-sm text-emerald-400" data-testid="credit-covered-row">
                <span className="flex items-center gap-1"><Check className="w-3 h-3" /> 1 film credit applied</span>
                <span className="font-mono">-${subtotal.toLocaleString()}</span>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-5 pt-4 border-t border-[#D4AF37]/20">
            <span className="text-white font-medium">Total due today</span>
            <span className="font-mono text-2xl text-[#D4AF37]" data-testid="order-total">${total.toLocaleString()}</span>
          </div>
          <Button onClick={checkout} disabled={busy}
            className="w-full mt-5 bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full h-12" data-testid="checkout-submit-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Proceed to Checkout"}
          </Button>
          <p className="text-xs text-slate-500 text-center mt-3">Card required only if total exceeds $0.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
